// Module 12's state container — a container, NOT an HTTP client (contract §3, ORP-18697).
//
// v3 ran four HTTP requests from inside this store and mirrored the whole thing into encrypted
// local storage. Both are gone: fetching lives in `service/integrationService.ts`, and this store
// declares no storage mirror at all. The bootstrap already carries every slice below, so mirroring
// bought nothing except a stale copy that outlived logout — v3's block named no field subset, so it
// wrote all six to disk (analysis §7.2).
//
// The spec greps this file for the HTTP-client and storage-mirror identifiers and expects zero hits,
// so describe them rather than spell them — the same rule the icon guardrail imposes repo-wide.
//
// The account-scoped half (`connectedIntegrations`, from the `integrations_connected` section) and
// the workspace-scoped half (the four slices, from the `integrations` section) are one store on
// purpose: they share a tenant and must reset together.
import { defineStore } from 'pinia'
import { ref } from 'vue'

import type {
  ConnectableIntegrationsDto,
  WorkspaceIntegrationDetailDto,
  WorkspaceIntegrationMap,
  WorkspaceIntegrationsDto,
} from '../models/integration.types'

export const useIntegrationStore = defineStore('integrations', () => {
  // ── Account-scoped: the typed `integrations_connected` section ──────────────────────────
  const accountId = ref<number | null>(null)
  const connectedIntegrations = ref<string[]>([])
  const isReady = ref(false)

  // ── Workspace-scoped: the `integrations` section (all four slices) ──────────────────────
  /** Every workspace's linked accounts. `null` = never hydrated (cold), not "empty". */
  const allIntegrations = ref<WorkspaceIntegrationsDto[] | null>(null)
  const allowedIntegrations = ref<string[]>([])
  /** Carried verbatim, never interpreted — the field's semantics are open (BE-12-08). */
  const integrationGroups = ref<Record<string, unknown[]> | null>(null)
  const connectableIntegrations = ref<ConnectableIntegrationsDto | null>(null)
  /**
   * `GET /integrations/{ws}/`'s counter block. `null` = never read, NOT "no connections".
   *
   * Held here rather than on the one screen that renders it so the connector pickers can cap their
   * selection at the remaining slots without each opening its own request — the number is one
   * account-wide fact, and two readers disagreeing about it is how a picker offers a slot the
   * backend then refuses.
   */
  const connectionUsage = ref<WorkspaceIntegrationDetailDto | null>(null)

  /** Clear prior-tenant access before accepting a different account's degraded bootstrap. */
  function selectAccount(nextAccountId: number): void {
    if (accountId.value !== null && accountId.value !== nextAccountId) reset()
    accountId.value = nextAccountId
  }

  function setConnectedIntegrations(keys: string[]): void {
    connectedIntegrations.value = [...keys]
    isReady.value = true
  }

  function hasIntegration(key: string): boolean {
    return connectedIntegrations.value.includes(key)
  }

  function setAllIntegrations(workspaces: WorkspaceIntegrationsDto[]): void {
    allIntegrations.value = workspaces
  }

  function setAllowedIntegrations(
    allowed: string[],
    groups: Record<string, unknown[]> | null,
  ): void {
    allowedIntegrations.value = [...allowed]
    integrationGroups.value = groups
  }

  function setConnectableIntegrations(connectable: ConnectableIntegrationsDto | null): void {
    connectableIntegrations.value = connectable
  }

  function setConnectionUsage(usage: WorkspaceIntegrationDetailDto | null): void {
    connectionUsage.value = usage
  }

  /**
   * One workspace's integration map, or `{}`.
   *
   * `workspaceId` is a PARAMETER, not a store read. v3's equivalent getter called
   * `useWorkspaceStore()` inside itself, which is what made it impossible to type or test. The `{}`
   * fallback is v3's `|| {}` and is load-bearing: a cold boot leaves `allIntegrations` null and
   * every caller iterates the result without a guard.
   *
   * Ids are compared as strings because the wire has sent this key both ways and `WorkspaceItem.id`
   * is `string | number`; v3's `===` silently returned `{}` whenever the two forms disagreed.
   */
  function integrationsFor(workspaceId: number | string | undefined): WorkspaceIntegrationMap {
    if (workspaceId === undefined || allIntegrations.value === null) return {}
    const key = String(workspaceId)
    return allIntegrations.value.find((entry) => String(entry.id) === key)?.integrations ?? {}
  }

  /** Clears EVERY field. Fires on logout, account switch and workspace switch. */
  function reset(): void {
    accountId.value = null
    connectedIntegrations.value = []
    isReady.value = false
    allIntegrations.value = null
    allowedIntegrations.value = []
    integrationGroups.value = null
    connectableIntegrations.value = null
    connectionUsage.value = null
  }

  return {
    accountId,
    connectedIntegrations,
    isReady,
    allIntegrations,
    allowedIntegrations,
    integrationGroups,
    connectableIntegrations,
    connectionUsage,
    selectAccount,
    setConnectedIntegrations,
    hasIntegration,
    setAllIntegrations,
    setAllowedIntegrations,
    setConnectableIntegrations,
    setConnectionUsage,
    integrationsFor,
    reset,
  }
})

export type IntegrationStore = ReturnType<typeof useIntegrationStore>
